import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { Sparkles, ArrowUpRight } from "lucide-react";
import { toast } from "sonner";

export default function Signup() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await register(form);
      toast.success("Account created.");
      const target = params.get("service") ? `/dashboard/new-order?service=${params.get("service")}&package=${params.get("package") || ""}` : "/dashboard";
      nav(target);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Signup failed");
    } finally {
      setLoading(false);
    }
  };

  const googleSignup = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2" data-testid="signup-page">
      <div className="hidden lg:flex flex-col justify-between p-12 bg-ink text-white relative overflow-hidden grain">
        <Link to="/" className="flex items-center gap-2">
          <div className="h-9 w-9 rounded-xl bg-white text-ink grid place-items-center"><Sparkles size={16} /></div>
          <span className="font-display font-bold text-lg">Zenith Ranking</span>
        </Link>
        <div>
          <div className="eyebrow !text-white/50 mb-3">Free to start</div>
          <h2 className="font-display text-4xl font-bold tracking-tight">Productized SEO and PR. Order in three minutes. Live in two weeks.</h2>
          <ul className="mt-6 space-y-2 text-white/80 text-sm">
            <li>· White-label fulfillment</li>
            <li>· Fixed prices, fixed scopes</li>
            <li>· Real publishers, no PBNs</li>
          </ul>
        </div>
        <div className="absolute -bottom-32 -left-20 h-96 w-96 rounded-full bg-terra/30 blur-3xl" />
      </div>

      <div className="flex items-center justify-center p-8 sm:p-12">
        <div className="w-full max-w-sm">
          <Link to="/" className="lg:hidden flex items-center gap-2 mb-8">
            <div className="h-8 w-8 rounded-xl bg-ink text-white grid place-items-center"><Sparkles size={14} /></div>
            <span className="font-display font-bold">Zenith Ranking</span>
          </Link>
          <div className="eyebrow mb-3">Create account</div>
          <h1 className="font-display text-3xl font-bold tracking-tight">Start your first campaign</h1>
          <p className="mt-2 text-sm text-inkSoft">Already a customer? <Link to="/login" className="text-terra hover:underline">Sign in</Link></p>

          <button onClick={googleSignup} type="button" className="mt-8 w-full inline-flex items-center justify-center gap-2 py-3 rounded-xl border border-line text-sm font-medium hover:bg-surfaceAlt transition" data-testid="google-signup-btn">
            <img src="https://www.gstatic.com/marketing-cms/assets/images/d5/dc/cfe9ce8b4425b410b49b7f2dd3f3/g.webp=s48-fcrop64=1,00000000ffffffff-rw" alt="" className="h-4 w-4" /> Continue with Google
          </button>
          <div className="my-6 flex items-center gap-3 text-xs text-inkSoft">
            <div className="flex-1 h-px bg-line" /> or email <div className="flex-1 h-px bg-line" />
          </div>
          <form onSubmit={submit} className="space-y-3" data-testid="signup-form">
            <Input label="Name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="signup-name" />
            <Input label="Email" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} testid="signup-email" />
            <Input label="Password" type="password" value={form.password} onChange={(v) => setForm({ ...form, password: v })} testid="signup-password" />
            <button type="submit" disabled={loading} className="btn-primary w-full justify-center mt-2" data-testid="signup-submit">
              {loading ? "Creating..." : (<>Create account <ArrowUpRight size={16} /></>)}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

function Input({ label, type = "text", value, onChange, testid }) {
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-widest text-inkSoft">{label}</span>
      <input
        type={type}
        required
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="mt-2 w-full h-11 px-4 rounded-xl border border-line bg-white text-sm outline-none focus:border-ink"
        data-testid={testid}
      />
    </label>
  );
}
